// src/lib/api_video.ts
// Cliente para el módulo Video del backend Django (Track D — D1→D3).
// Mirrors apps/video/schemas.py — /admin/video/**.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init, headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 409) throw new ApiError(detail || 'Conflicto.', 409);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface Paginated<T> { items: T[]; count: number }

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type VideoProvider = 'remotion' | 'fal_ai' | 'manual';
export type VideoStatus =
  | 'draft' | 'pending' | 'rendering' | 'completed' | 'failed' | 'cancelled';

export interface VideoTemplate {
  id: number;
  org_id: string;
  name: string;
  slug: string;
  provider: VideoProvider;
  composition_id: string;
  fal_endpoint: string;
  prop_schema: Record<string, unknown>;
  default_props: Record<string, unknown>;
  description: string;
  duration_seconds: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  preview_bunny_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VideoTemplateIn {
  name: string;
  slug: string;
  provider: VideoProvider;
  composition_id?: string;
  fal_endpoint?: string;
  prop_schema?: Record<string, unknown>;
  default_props?: Record<string, unknown>;
  description?: string;
  duration_seconds?: number | null;
  width?: number | null;
  height?: number | null;
  fps?: number | null;
  preview_bunny_url?: string;
  is_active?: boolean;
}

export interface Video {
  id: number;
  org_id: string;
  template_id: number | null;
  provider: VideoProvider;
  title: string;
  props: Record<string, unknown>;
  prompt: string;
  status: VideoStatus;
  external_job_id: string;
  error_message: string;
  requested_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  source_url: string;
  bunny_url: string;
  media_mime_type: string;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  file_size_bytes: number | null;
  created_at: string;
  updated_at: string;
}

export interface VideoIn {
  provider: VideoProvider;
  template_id?: number | null;
  title?: string;
  props?: Record<string, unknown>;
  prompt?: string;
  bunny_url?: string;
}

export interface VideoActionOut {
  video: Video;
  status: 'queued' | 'cancelled' | 'skipped' | 'error';
  task_id?: string | null;
  detail?: string;
}

// ═══════════════════════════════════════════════════════════════
// Templates
// ═══════════════════════════════════════════════════════════════

export async function listTemplates(
  filters: {
    provider?: VideoProvider; is_active?: boolean; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<VideoTemplate>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<VideoTemplate>>(
    `/api/v1/admin/video/templates${qs({ ...rest, limit, offset })}`,
  );
}

export async function getTemplate(id: number): Promise<VideoTemplate> {
  return djangoFetch<VideoTemplate>(`/api/v1/admin/video/templates/${id}`);
}

export async function createTemplate(data: VideoTemplateIn): Promise<VideoTemplate> {
  return djangoFetch<VideoTemplate>('/api/v1/admin/video/templates', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function updateTemplate(id: number, data: Partial<VideoTemplateIn>): Promise<VideoTemplate> {
  return djangoFetch<VideoTemplate>(`/api/v1/admin/video/templates/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
}

export async function deleteTemplate(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/video/templates/${id}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════════
// Videos
// ═══════════════════════════════════════════════════════════════

export async function listVideos(
  filters: {
    provider?: VideoProvider; status?: VideoStatus; template_id?: number; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<Video>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<Video>>(
    `/api/v1/admin/video/videos${qs({ ...rest, limit, offset })}`,
  );
}

export async function getVideo(id: number): Promise<Video> {
  return djangoFetch<Video>(`/api/v1/admin/video/videos/${id}`);
}

export async function createVideo(data: VideoIn): Promise<Video> {
  return djangoFetch<Video>('/api/v1/admin/video/videos', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function deleteVideo(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/video/videos/${id}`, { method: 'DELETE' });
}

export async function renderVideo(id: number): Promise<VideoActionOut> {
  return djangoFetch<VideoActionOut>(
    `/api/v1/admin/video/videos/${id}/render`,
    { method: 'POST', body: '{}' },
  );
}

export async function cancelVideo(id: number): Promise<Video> {
  return djangoFetch<Video>(
    `/api/v1/admin/video/videos/${id}/cancel`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
